import { useMemo, useState, useEffect } from 'react';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import useProductDetails from 'modules/queries/product/useDetails';
import { optionValues } from 'utility/products/options';
import type { DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import { getCloudOptions } from 'modules/backups/helpers';
import CloudDailyBackupSettings from './Daily';
import CloudQuotaBackupSettings from './Quota';

interface CloudBackupSettingsProps extends DrawerProps {
	assetDetails: AssetDetails;
	onDestroy: () => void;
	onClose: () => void;
}

const CloudBackupSettings: React.FC<CloudBackupSettingsProps> = ({
	assetDetails,
	...rest
}) => {
	const { data: assetProductDetails, isLoading } = useProductDetails(
		{
			code: assetDetails?.type || '',
		},
		{ enabled: Boolean(assetDetails?.type) },
	);

	const availableLiquidWebBackupPlans = useMemo(
		() => optionValues('LiquidWebBackupPlan', assetProductDetails?.options),
		[assetProductDetails],
	);

	const backupOptions = useMemo(() => {
		if (
			assetDetails?.backupsState &&
			availableLiquidWebBackupPlans?.length > 0
		) {
			return getCloudOptions(assetDetails, availableLiquidWebBackupPlans);
		}
		return [];
	}, [assetDetails, availableLiquidWebBackupPlans]);

	const { backupPlan } = assetDetails;

	const [backupTypeOption, setBackuptypeOption] = useState<
		string | undefined
	>();

	// initialize backupTypeOption state based on computed backupOptions
	useEffect(() => {
		if (!backupTypeOption) {
			const backupOptionValue =
				backupOptions.find((opt) => opt.value === backupPlan)?.value ??
				backupOptions[0]?.value;
			setBackuptypeOption(backupOptionValue);
		}
	}, [backupOptions, backupPlan, backupTypeOption]);

	const handleBackupTypeChange = (value: string) => {
		setBackuptypeOption(value);
	};

	if (isLoading || !backupTypeOption) {
		return (
			<Stack flex={1} justifyContent="center" alignItems="center">
				<LoaderCircleIcon
					sx={({ size }) => ({ height: size.lg, width: size.lg })}
				/>
			</Stack>
		);
	}

	if (backupTypeOption === 'Quota') {
		return (
			<CloudQuotaBackupSettings
				assetDetails={assetDetails}
				backupOptions={backupOptions}
				backupType={backupTypeOption}
				onBackupTypeChange={handleBackupTypeChange}
				{...rest}
			/>
		);
	}

	return (
		<CloudDailyBackupSettings
			assetDetails={assetDetails}
			backupOptions={backupOptions}
			backupType={backupTypeOption}
			onBackupTypeChange={handleBackupTypeChange}
			{...rest}
		/>
	);
};

export default CloudBackupSettings;
